const WINDOWS_RESERVED_FILENAME_BASES = new Set([
  'CON', 'PRN', 'AUX', 'NUL',
  ...Array.from({ length: 9 }, (_, index) => `COM${index + 1}`),
  ...Array.from({ length: 9 }, (_, index) => `LPT${index + 1}`),
]);
const MAX_FILENAME_BASE_LENGTH = 200;

type FilenameCandidate = string | number | null | undefined;

export type PrintRunFilenameSource = {
  id?: FilenameCandidate;
  jobId?: FilenameCandidate;
  jobName?: FilenameCandidate;
  packageLabel?: FilenameCandidate;
  packageTag?: FilenameCandidate;
};

// A length limit must not leave half of a surrogate pair at the end.
const truncateFilenameValue = (value: string, maxLength: number) =>
  value.slice(0, maxLength).replace(/[\uD800-\uDBFF]$/u, '');

const isWindowsReservedFilenameBase = (value: string) =>
  WINDOWS_RESERVED_FILENAME_BASES.has(value.split('.')[0].toUpperCase());

const sanitizeFilenameCandidate = (candidate: FilenameCandidate, preserveFullValue = false) => {
  const cleaned = String(candidate ?? '')
    .normalize('NFC')
    .trim()
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/[\p{Cc}\p{Cf}]/gu, '');
  return truncateFilenameValue(cleaned, preserveFullValue ? Infinity : MAX_FILENAME_BASE_LENGTH)
    .replace(/^\.+/g, '')
    .replace(/[. ]+$/g, '');
};

const firstValidFilenameCandidate = (candidates: FilenameCandidate[], preserveFullValue = false) => {
  for (const candidate of candidates) {
    const sanitized = sanitizeFilenameCandidate(candidate, preserveFullValue);
    if (sanitized && !isWindowsReservedFilenameBase(sanitized)) return sanitized;
  }
  return '';
};

const filenameBaseFromCandidates = (...candidates: FilenameCandidate[]) =>
  firstValidFilenameCandidate(candidates) || 'print';

export const printRunFilenameBase = (jobName?: string, jobId?: string) =>
  filenameBaseFromCandidates(jobName, jobId);

const packageFilenameBase = (jobName: string, packageLabel: string, fallback: string) => {
  if (!jobName) return packageLabel || fallback;
  const combinedBase = packageLabel ? `${jobName}-${packageLabel}` : jobName;
  const safeJobName = isWindowsReservedFilenameBase(combinedBase)
    ? sanitizeFilenameCandidate(`_${jobName}`)
    : jobName;
  if (!packageLabel) return safeJobName;
  const minimumPrefixLength = Array.from(safeJobName)[0].length;
  const prefix = truncateFilenameValue(safeJobName, Math.max(minimumPrefixLength, MAX_FILENAME_BASE_LENGTH - packageLabel.length - 1));
  return `${prefix}-${packageLabel}`;
};

export const printRunPackageFilenameBase = (jobName?: string, packageLabel?: string | null, jobId?: string) =>
  packageFilenameBase(
    sanitizeFilenameCandidate(jobName),
    firstValidFilenameCandidate([packageLabel], true),
    printRunFilenameBase(jobName, jobId),
  );

export const printRunCsvFilename = (jobName?: string, jobId?: string, packageLabel?: string | null) =>
  `${printRunPackageFilenameBase(jobName, packageLabel, jobId)}.csv`;

// The Prints list shows Job Name and Job ID from the listed row, so a fetched record missing either must fall back to what the user can see.
export const printRunRecordFilenameBase = (
  fetched?: PrintRunFilenameSource | null,
  listed?: PrintRunFilenameSource | null,
) => {
  const runId = fetched?.id ?? listed?.id;

  return filenameBaseFromCandidates(
    fetched?.jobName,
    listed?.jobName,
    fetched?.jobId,
    listed?.jobId,
    runId == null ? undefined : `print-run-${runId}`,
  );
};

export const printRunRecordCsvFilename = (
  fetched?: PrintRunFilenameSource | null,
  listed?: PrintRunFilenameSource | null,
) => `${printRunRecordPackageFilenameBase(fetched, listed)}.csv`;

/** Resolves a filename-safe Package UID, including the legacy packageTag alias. */
export const printRunRecordPackageLabel = (
  fetched?: PrintRunFilenameSource | null,
  listed?: PrintRunFilenameSource | null,
  resolvedPackageLabel?: string,
) => firstValidFilenameCandidate([
  fetched?.packageLabel,
  fetched?.packageTag,
  listed?.packageLabel,
  listed?.packageTag,
  resolvedPackageLabel,
], true);

export const printRunRecordPackageFilenameBase = (
  fetched?: PrintRunFilenameSource | null,
  listed?: PrintRunFilenameSource | null,
  resolvedPackageLabel?: string,
) => packageFilenameBase(
  sanitizeFilenameCandidate(fetched?.jobName) || sanitizeFilenameCandidate(listed?.jobName),
  printRunRecordPackageLabel(fetched, listed, resolvedPackageLabel),
  printRunRecordFilenameBase(fetched, listed),
);
